import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { HelperText, Text, useTheme } from 'react-native-paper';
import { containsOneLowercaseCharacter, containsOneNumber, containsOneSpecialCharacter, containsOneUppercaseCharacter, eightCharacterMinimum, isRequired, isValidUserName, minAndMaxLength, PASSWORD_MUST_MATCH_MESSAGE, sameAs, } from '@luminate/luminate-ts-sdk';
import { LabeledSwitch, PasswordInput, ValidatedInput, } from '../common';
import { useValidated } from '../hooks';
const validUsernameLength = (value) => {
    return minAndMaxLength({ data: value, min: 6, max: 64 });
};
const DefaultUsernameError = ({ testID = 'default-username-error' }) => {
    const theme = useTheme();
    return (_jsx(HelperText, { testID: `${testID}-text`, type: 'error', style: { color: theme.colors.onError }, children: "The username supplied is not valid. Please select another username." }));
};
const DefaultPasswordError = ({ testID = 'default-password-error' }) => {
    const theme = useTheme();
    return (_jsx(HelperText, { testID: `${testID}-text`, type: 'error', style: { color: theme.colors.onError }, children: "The password supplied does not meet our minimum security standards. Please select another password." }));
};
export const AccountRegistrationCredentials = forwardRef(({ testID = 'account-registration-credentials', disabled = false, usernameValid = true, usernameIsEmail = true, usernameRules = [isRequired, isValidUserName, validUsernameLength], usernameError = _jsx(DefaultUsernameError, { testID: `${testID}-default-username-error` }), password = '', passwordRules = [
    eightCharacterMinimum,
    containsOneSpecialCharacter,
    containsOneLowercaseCharacter,
    containsOneUppercaseCharacter,
    containsOneNumber,
], isPasswordValid = true, passwordError = _jsx(DefaultPasswordError, { testID: `${testID}-default-password-error` }), confirmPassword = '', confirmPasswordRules = [
    /** Password Must Match as the default rule for Confirm Password Rules **/
    () => sameAs({
        first: password,
        second: confirmPassword,
    }, PASSWORD_MUST_MATCH_MESSAGE),
], ...props }, ref) => {
    const theme = useTheme();
    const styles = StyleSheet.create({
        inputSpacing: {
            marginVertical: 5,
        },
        errorCard: {
            marginVertical: 5,
            borderWidth: 1,
            borderColor: theme.colors.errorContainer,
            backgroundColor: theme.colors.error,
        },
        guardianDialog: {
            backgroundColor: theme.colors.secondaryContainer,
        },
    });
    const [reValidateConfirmPassword, setReValidateConfirmPassword] = useState(false);
    const [usernameErrors, onUserNameValidated] = useValidated();
    const [passwordErrors, onPasswordValidated] = useValidated(() => {
        setReValidateConfirmPassword(true);
    });
    const [confirmPasswordErrors, onConfirmPasswordValidated] = useValidated(() => {
        setReValidateConfirmPassword(true);
    });
    useEffect(() => {
        if (props.onValidationChanged) {
            props.onValidationChanged(usernameErrors.concat(passwordErrors, confirmPasswordErrors));
        }
    }, [usernameErrors, passwordErrors, confirmPasswordErrors]);
    const usernameRef = useRef(null);
    const passwordRef = useRef(null);
    const confirmPasswordRef = useRef(null);
    const validateAllInputs = () => {
        const isUsernameValid = usernameRef.current?.isValid() ?? true;
        const isPasswordValid = passwordRef.current?.isValid() ?? true;
        const isConfirmPasswordValid = confirmPasswordRef.current?.isValid() ?? true;
        return isUsernameValid && isPasswordValid && isConfirmPasswordValid;
    };
    useImperativeHandle(ref, () => ({
        isValid: validateAllInputs,
    }));
    return (_jsxs(View, { testID: `${testID}-container`, children: [_jsx(LabeledSwitch, { testID: `${testID}-username-is-email`, style: styles.inputSpacing, disabled: disabled, value: usernameIsEmail, onValueChanged: props.onUsernameIsEmailChanged, right: _jsx(View, { testID: `${testID}-user-is-email-description-container`, children: _jsx(Text, { testID: `${testID}-user-is-email-description-text`, style: { marginHorizontal: 10 }, numberOfLines: 2, children: "Use email address for username (recommended)" }) }) }), _jsx(ValidatedInput, { ref: usernameRef, testID: `${testID}-username`, label: 'Username', disabled: disabled || usernameIsEmail, style: styles.inputSpacing, autoCorrect: false, reValidate: usernameIsEmail, autoCapitalize: 'none', value: props.username, onChangeText: props.onUsernameChanged, rules: usernameRules, errors: usernameIsEmail ? [] : usernameErrors, onValidate: onUserNameValidated }), !usernameValid && usernameError, _jsx(PasswordInput, { ref: passwordRef, testID: `${testID}-password`, label: 'Password', disabled: disabled, style: styles.inputSpacing, value: password, onChangeText: props.onPasswordChanged, rules: passwordRules, errors: passwordErrors, onValidate: onPasswordValidated, reValidate: reValidateConfirmPassword }), _jsx(PasswordInput, { ref: confirmPasswordRef, testID: `${testID}-confirm-password`, label: 'Confirm Password', disabled: disabled, style: styles.inputSpacing, value: confirmPassword, onChangeText: props.onConfirmPasswordChanged, rules: confirmPasswordRules, errors: confirmPasswordErrors, onValidate: onConfirmPasswordValidated, reValidate: reValidateConfirmPassword }), !isPasswordValid && passwordError] }));
});
